import { CircleIcon, FunnelIcon } from "@phosphor-icons/react";
import { useMemo } from "react";
import {
  getLoadingDynamicFilterCategories,
  getSelectedDynamicFiltersWithLabels,
  getSelectedKeyMessagesWithLabels,
  getSelectedStaticFiltersWithLabels,
} from "@/components/news-feed/utils/filter-lookups";
import GenericFilterPill from "@/components/ui/data-display/generic-filter-pill";
import type { MenuItemConfig } from "@/components/ui/menu-item";
import type { ShareVoiceSidebarCategory } from "@/data-access/share-of-voice/queries/shareOfVoice-sidebar-filter-queries";
import { DISCOVERED_TAG_CATEGORIES } from "@/lib/constants/discovered-tag-categories";
import { useFeature } from "@/lib/feature-management";
import { cn } from "@/lib/utils";
import { normalizeCategoryForDisplay } from "@/lib/utils/filters";
import useFilterDictionary from "@/store/filter-dictionary/filter-dictionary.slice";
import useNewsFeedFilters from "@/store/news-feed/filters/newsFeed-filter.slice";
import useKeyMessageStore from "@/store/news-feed/key-messages/key-message.slice";
import { useAnalysisParamsContext } from "@/store/news-feed/params/analysis-params.context";

/**
 * Applied filters for the Analysis route. Reads selected filters from URL (context),
 * resolves labels via filter-dictionary, newsFeed-filters, and key-message stores,
 * and clears by updating the URL via {@link AnalysisParamsProvider}'s navigateWithSearch.
 * Use only inside AnalysisParamsProvider.
 */
const AnalysisAppliedFilters = () => {
  const analysisParams = useAnalysisParamsContext();
  const fetchOnExpand = useFeature("fetch-dynamic-filters-exp", false);
  const discoveredTagsHidden = useFeature("discovered-tags-hidden", false);
  const filterDictionaryState = useFilterDictionary((state) => state);
  const newsFeedFiltersState = useNewsFeedFilters((state) => state);
  const keyMessagesFromStore = useKeyMessageStore((state) => state.keyMessages);
  const selectedKeyMessageFilterExclusion = useKeyMessageStore(
    (state) => state.selectedKeyMessageFilterExclusion,
  );
  const updateSelectedKeyMessageFilterExclusion = useKeyMessageStore(
    (state) => state.updateSelectedKeyMessageFilterExclusion,
  );

  const effectiveKeyMessageConjunction =
    analysisParams?.keyMessageConjunction ?? selectedKeyMessageFilterExclusion;

  const selectedFiltersMap = useMemo(() => {
    if (!analysisParams?.selectedFilters) {
      return new Map<ShareVoiceSidebarCategory, string[]>();
    }
    const m = new Map<ShareVoiceSidebarCategory, string[]>();
    for (const [k, v] of Object.entries(analysisParams.selectedFilters)) {
      const category = k as ShareVoiceSidebarCategory;
      if (discoveredTagsHidden && DISCOVERED_TAG_CATEGORIES.has(category)) {
        continue;
      }
      if (v?.length) m.set(category, v);
    }
    return m;
  }, [analysisParams?.selectedFilters, discoveredTagsHidden]);

  const staticFilters = useMemo(
    () =>
      getSelectedStaticFiltersWithLabels(
        selectedFiltersMap,
        filterDictionaryState,
      ),
    [selectedFiltersMap, filterDictionaryState],
  );

  const dynamicFilters = useMemo(
    () =>
      getSelectedDynamicFiltersWithLabels(
        selectedFiltersMap,
        newsFeedFiltersState,
      ),
    [selectedFiltersMap, newsFeedFiltersState],
  );

  const loadingDynamicCategories = useMemo(
    () =>
      getLoadingDynamicFilterCategories(
        selectedFiltersMap,
        dynamicFilters,
        newsFeedFiltersState,
        fetchOnExpand,
      ),
    [selectedFiltersMap, dynamicFilters, newsFeedFiltersState, fetchOnExpand],
  );

  const keyMessages = useMemo(() => {
    const ids = selectedFiltersMap.get("KEY_MESSAGE") ?? [];
    return getSelectedKeyMessagesWithLabels(ids, {
      keyMessages: keyMessagesFromStore,
    });
  }, [selectedFiltersMap, keyMessagesFromStore]);

  const onClearCategory = (category: ShareVoiceSidebarCategory) => {
    if (!analysisParams) return;
    const next = { ...analysisParams.selectedFilters };
    delete next[category];
    if (discoveredTagsHidden) {
      for (const discoveredTagCategory of DISCOVERED_TAG_CATEGORIES) {
        delete next[discoveredTagCategory];
      }
    }
    analysisParams.navigateWithSearch({ selected: next });
  };

  const keyMessageMenuItems: MenuItemConfig[] = [
    {
      id: "filter-exclusion-and",
      icon: (
        <CircleIcon
          className={cn(
            "size-2",
            effectiveKeyMessageConjunction === "AND"
              ? "fill-current"
              : "fill-none",
          )}
        />
      ),
      label: "AND",
      onClick: () => {
        updateSelectedKeyMessageFilterExclusion("AND");
        analysisParams?.navigateWithSearch({ keyMessageConjunction: "AND" });
      },
      className: "text-xxs pl-2",
    },
    {
      id: "filter-exclusion-or",
      icon: (
        <CircleIcon
          className={cn(
            "size-2",
            effectiveKeyMessageConjunction === "OR"
              ? "fill-current"
              : "fill-none",
          )}
        />
      ),
      label: "OR",
      onClick: () => {
        updateSelectedKeyMessageFilterExclusion("OR");
        analysisParams?.navigateWithSearch({ keyMessageConjunction: "OR" });
      },
      className: "text-xxs pl-2",
    },
  ];

  const totalFilterPills =
    staticFilters.size +
    dynamicFilters.size +
    loadingDynamicCategories.length +
    keyMessages.size;
  const shouldMoveKeyMessageToNewLine =
    totalFilterPills >= 3 && keyMessages.size > 4;

  const keyMessagePills = Array.from(keyMessages.entries()).map(
    ([category, selected]: [string, string[]]) => (
      <GenericFilterPill
        key={category}
        category={normalizeCategoryForDisplay(category)}
        selectedOptions={selected}
        onClear={() => onClearCategory(category as ShareVoiceSidebarCategory)}
        menuItems={keyMessageMenuItems}
        conjunction={effectiveKeyMessageConjunction === "AND" ? "and" : "or"}
      />
    ),
  );

  if (!analysisParams) return null;

  return (
    <div className="flex flex-row gap-2 flex-wrap items-center">
      <div className="flex items-center gap-2">
        <FunnelIcon size={16} />
        <span className="text-xs text-gray-500">Filters Applied: </span>
      </div>
      {Array.from(staticFilters.entries()).map(([category, selected]) => (
        <GenericFilterPill
          key={category}
          category={normalizeCategoryForDisplay(category)}
          selectedOptions={selected}
          onClear={() => onClearCategory(category)}
        />
      ))}
      {Array.from(dynamicFilters.entries()).map(([category, selected]) => (
        <GenericFilterPill
          key={category}
          category={normalizeCategoryForDisplay(category)}
          selectedOptions={selected}
          countOnly={category === "NARRATIVE_CLUSTER"}
          onClear={() => onClearCategory(category as ShareVoiceSidebarCategory)}
        />
      ))}
      {loadingDynamicCategories.map((category) => (
        <GenericFilterPill
          key={`loading-${category}`}
          category={normalizeCategoryForDisplay(category)}
          selectedOptions={[]}
          isLoading
          countOnly={category === "NARRATIVE_CLUSTER"}
          onClear={() => onClearCategory(category)}
        />
      ))}
      {shouldMoveKeyMessageToNewLine ? (
        <div className="w-full basis-full">{keyMessagePills}</div>
      ) : (
        keyMessagePills
      )}
    </div>
  );
};

export default AnalysisAppliedFilters;
